import { forwardRef, memo, type ReactNode } from 'react';

import { EllipsisVerticalIcon, XIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { type SelectableItem } from '@air/redux-selected-items';

import { AgentSelectionBarCtaActions } from '~/components/v2/components/private-selection-bar/agent-selection-bar/AgentSelectionBarCtaActions';
import { AgentSelectionBarItemCount } from '~/components/v2/components/private-selection-bar/agent-selection-bar/AgentSelectionBarItemCount';
import { AgentSelectionBarPrefixActions } from '~/components/v2/components/private-selection-bar/agent-selection-bar/AgentSelectionBarPrefixActions';
import { AgentSelectionBarThumbnailStrip } from '~/components/v2/components/private-selection-bar/agent-selection-bar/AgentSelectionBarThumbnailStrip';
import { AgentDropdownMenu } from '~/components/v2/components/private-selection-bar/agent-selection-bar/agent-dropdown-menu/AgentDropdownMenu';
import { renderAgentDropdownItems } from '~/components/v2/components/private-selection-bar/agent-selection-bar/agent-dropdown-menu/renderAgentDropdownItems';
import { type SelectionBarControlMeasureRefs } from '~/components/v2/components/private-selection-bar/hooks/useSelectionBarControlWidths';
import { getAgentSelectionBarLayoutTierAttribute } from '~/components/v2/components/private-selection-bar/hooks/useSelectionBarLayoutTier';
import { type SelectionBarActions } from '~/components/v2/components/shared/selectionBar/selectionBarActions';
import { SELECTION_ACTION_BAR_ID } from '~/constants/selectionActionBar';

/**
 * Everything the bar draws, in one object.
 *
 * Kept whole rather than spread across props so `PrivateAgentSelectionBar` can hold the lot still
 * with a single freeze while the shell collapses — see its comment.
 */
export type AgentSelectionBarViewModel = {
  selectedItems: SelectableItem[];
  countLabel: string;
  scopeLabel?: string;
  actions: SelectionBarActions;
  menuOptions: DropdownMenuOption[];
  hasSelection: boolean;
  showSelectAll: boolean;
  selectAllLabel: string;
  isSelectAllActive: boolean;
  showIncludeNestedItems: boolean;
  showSelectionDropdown: boolean;
  compactOutlineCtAs: boolean;
  hidePrefixActions: boolean;
  hideThumbStrip: boolean;
  hideScopeLabel: boolean;
  /** Sits in the Download/Share cluster (same 8px gap). Selection-bar Start task. */
  endCta?: ReactNode;
};

export type AgentSelectionBarProps = {
  viewModel: AgentSelectionBarViewModel;
  controlMeasureRefs?: SelectionBarControlMeasureRefs;
  onClearAll: () => void;
  onToggleSelectAll: () => void;
  onIncludeNestedItems: () => void;
};

/**
 * The selection bar as it appears inside the agent space.
 *
 * Brings no surface of its own — `WorkspaceAgentSpaceShell` supplies the glass, radius, blur and
 * entrance — and its copy is light-on-dark in both themes, so nothing here uses semantic tokens
 * (`text-gray-1` would invert to near-black on near-black in dark mode).
 *
 * Draws only what it is handed. Reading the selection itself would tie it to a store that empties
 * the instant the user clears, which is the one moment it still needs to be on screen.
 */
export const AgentSelectionBar = memo(
  forwardRef<HTMLDivElement, AgentSelectionBarProps>((props, ref) => {
    const { controlMeasureRefs, viewModel, onClearAll, onToggleSelectAll, onIncludeNestedItems } = props;
    const {
      actions,
      compactOutlineCtAs,
      countLabel,
      endCta,
      hasSelection,
      hidePrefixActions,
      hideScopeLabel,
      hideThumbStrip,
      isSelectAllActive,
      menuOptions,
      scopeLabel,
      selectAllLabel,
      selectedItems,
      showIncludeNestedItems,
      showSelectAll,
      showSelectionDropdown,
    } = viewModel;

    if (!hasSelection) {
      return null;
    }

    return (
      <div
        ref={ref}
        aria-label="Selection actions"
        className="flex w-full min-w-0 items-center justify-between gap-2 overflow-hidden p-2 ads:text-inverse text-white sm:gap-3"
        data-disableselect="true"
        data-layout-tier={getAgentSelectionBarLayoutTierAttribute({
          compactOutlineCtAs,
          hideIncludeNestedButton: false,
          hidePrefixActions,
          hideScopeLabel,
          hideSelectAllButton: false,
          hideThumbStrip,
        })}
        data-testid="SELECTION_ACTION_BAR"
        id={SELECTION_ACTION_BAR_ID}
        role="toolbar"
      >
        <div
          className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden"
          data-testid="SELECTION_ACTION_BAR_LEFT_GROUP"
        >
          <Tooltip label="Clear selection">
            <IconButton
              adsIcon
              appearance="ghost"
              className="shrink-0 rounded-full bg-transparent ads:text-inverse/75 text-white/75 hover:ads:bg-ads-white/10 hover:bg-white/10 hover:ads:text-inverse hover:text-white active:ads:bg-ads-white/15 active:bg-white/15"
              data-testid="SELECTION_ACTION_BAR_CLEAR_BTN"
              icon={XIcon}
              label="Clear selection"
              onClick={onClearAll}
              size="medium"
            />
          </Tooltip>

          {!hideThumbStrip && <AgentSelectionBarThumbnailStrip items={selectedItems} />}

          <AgentSelectionBarItemCount
            countLabel={countLabel}
            hideScopeLabel={hideScopeLabel}
            itemCountMinMeasureRef={controlMeasureRefs?.itemCountMin}
            scopeLabel={scopeLabel}
            scopeMeasureRef={controlMeasureRefs?.scope}
            selectedItemCount={selectedItems.length}
            showSelectionDropdown={showSelectionDropdown}
          />

          <span aria-hidden className="min-w-0 flex-1" />

          {showSelectAll && (
            <Button
              ref={controlMeasureRefs?.selectAll}
              appearance="ghost"
              aria-pressed={isSelectAllActive}
              className="shrink-0 rounded-6 bg-transparent font-medium ads:text-inverse/75 text-white/75 hover:ads:bg-ads-white/10 hover:bg-white/10 hover:ads:text-inverse hover:text-white active:ads:bg-ads-white/15 active:bg-white/15"
              data-testid="SELECTION_ACTION_BAR_SELECT_ALL"
              onClick={onToggleSelectAll}
              size="small"
            >
              {selectAllLabel}
            </Button>
          )}

          {showIncludeNestedItems && (
            <Button
              appearance="ghost"
              className="shrink-0 rounded-6 bg-transparent font-medium ads:text-inverse/75 text-white/75 hover:ads:bg-ads-white/10 hover:bg-white/10 hover:ads:text-inverse hover:text-white active:ads:bg-ads-white/15 active:bg-white/15"
              onClick={onIncludeNestedItems}
              size="small"
            >
              Include nested items
            </Button>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 overflow-hidden">
          <div className="flex items-center gap-0.5">
            {!hidePrefixActions && <AgentSelectionBarPrefixActions actions={actions} />}

            {menuOptions.length > 0 && (
              <AgentDropdownMenu
                data-testid="AGENT_SELECTION_BAR_MORE_ACTIONS_DROPDOWN_MENU"
                trigger={
                  <IconButton
                    adsIcon
                    appearance="ghost"
                    className="shrink-0 rounded-6 bg-transparent ads:text-inverse/75 text-white/75 hover:ads:bg-ads-white/10 hover:bg-white/10 hover:ads:text-inverse hover:text-white active:ads:bg-ads-white/15 active:bg-white/15"
                    data-testid="SELECTION_ACTION_BAR_MORE_ACTIONS"
                    icon={EllipsisVerticalIcon}
                    label="More actions"
                    size="small"
                  />
                }
                triggerTooltip="More actions"
              >
                {renderAgentDropdownItems({ options: menuOptions })}
              </AgentDropdownMenu>
            )}
          </div>

          <AgentSelectionBarCtaActions actions={actions} compactOutlineCtAs={compactOutlineCtAs} />
          {endCta}
        </div>
      </div>
    );
  }),
);

AgentSelectionBar.displayName = 'AgentSelectionBar';
